import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "./lib/auth";
import { SchoolProvider } from "./lib/school";
import { ThemeProvider, useTheme } from "./lib/theme";
import { ConfirmProvider } from "./lib/confirm";
import { ChatProvider } from "./lib/chat";
import { lazyPage, PageLoading, PageErrorBoundary } from "./lib/lazyPage";

const Landing = lazyPage(() => import("./pages/Landing"));
const Login = lazyPage(() => import("./pages/Login"));
const AppShell = lazyPage(() => import("./components/AppShell"));
const Dashboard = lazyPage(() => import("./pages/Dashboard"));
const Athletes = lazyPage(() => import("./pages/Athletes"));
const AthleteProfile = lazyPage(() => import("./pages/AthleteProfile"));
const RecruitProfile = lazyPage(() => import("./pages/RecruitProfile"));
const RecruitTalkingPoints = lazyPage(() => import("./pages/RecruitTalkingPoints"));
const Academics = lazyPage(() => import("./pages/Academics"));
const Compliance = lazyPage(() => import("./pages/Compliance"));
const Recruiting = lazyPage(() => import("./pages/Recruiting"));
const NIL = lazyPage(() => import("./pages/NIL"));
const Strength = lazyPage(() => import("./pages/Strength"));
const SportsMedicine = lazyPage(() => import("./pages/SportsMedicine"));
const Operations = lazyPage(() => import("./pages/Operations"));
const Fundraising = lazyPage(() => import("./pages/Fundraising"));
const Communications = lazyPage(() => import("./pages/Communications"));
const Ticketing = lazyPage(() => import("./pages/Ticketing"));
const Marketing = lazyPage(() => import("./pages/Marketing"));
const BusinessOffice = lazyPage(() => import("./pages/BusinessOffice"));
const Facilities = lazyPage(() => import("./pages/Facilities"));
const Wellness = lazyPage(() => import("./pages/Wellness"));
const Development = lazyPage(() => import("./pages/Development"));
const VideoCreative = lazyPage(() => import("./pages/VideoCreative"));
const SportsInformation = lazyPage(() => import("./pages/SportsInformation"));
const Nutrition = lazyPage(() => import("./pages/Nutrition"));
const Security = lazyPage(() => import("./pages/Security"));
const Equipment = lazyPage(() => import("./pages/Equipment"));
const AuditLog = lazyPage(() => import("./pages/AuditLog"));
const ActivityFeed = lazyPage(() => import("./pages/ActivityFeed"));
const Messages = lazyPage(() => import("./pages/Messages"));
const CalendarPage = lazyPage(() => import("./pages/CalendarPage"));
const TasksPage = lazyPage(() => import("./pages/TasksPage"));
const Staff = lazyPage(() => import("./pages/Staff"));
const SportPage = lazyPage(() => import("./pages/SportPage"));
const Settings = lazyPage(() => import("./pages/Settings"));
const Schools = lazyPage(() => import("./pages/Schools"));
const PublicQuestionnaire = lazyPage(() => import("./pages/PublicQuestionnaire"));
const PublicRecruitQuestionnaire = lazyPage(() => import("./pages/PublicRecruitQuestionnaire"));
const PublicBlast = lazyPage(() => import("./pages/PublicBlast"));
const BusCheckin = lazyPage(() => import("./pages/BusCheckin"));
const PitchDeck = lazyPage(() => import("./pages/PitchDeck"));
const ForgotPassword = lazyPage(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyPage(() => import("./pages/ResetPassword"));
const AcceptInvite = lazyPage(() => import("./pages/AcceptInvite"));
const StaffAccess = lazyPage(() => import("./pages/StaffAccess"));

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          LOADING OMNI...
        </div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  return children;
}

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster theme={theme} position="top-right" />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SchoolProvider>
          <ChatProvider>
          <BrowserRouter>
            <ConfirmProvider>
            <ThemedToaster />
            <PageErrorBoundary>
            <Suspense fallback={<PageLoading full />}>
            <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Navigate to="/login" replace />} />
            <Route path="/q/:token" element={<PublicQuestionnaire />} />
            <Route path="/rq/:token" element={<PublicRecruitQuestionnaire />} />
            <Route path="/m/:token" element={<PublicBlast />} />
            <Route path="/bus/:token" element={<BusCheckin />} />
            <Route path="/pitch" element={<PitchDeck />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route
              path="/app"
              element={
                <Protected>
                  <AppShell />
                </Protected>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="athletes" element={<Athletes />} />
              <Route path="athletes/:id" element={<AthleteProfile />} />
              <Route path="academics" element={<Academics />} />
              <Route path="compliance" element={<Compliance />} />
              <Route path="recruiting" element={<Recruiting />} />
              <Route path="recruits/:id" element={<RecruitProfile />} />
              <Route path="recruits/:id/talking-points" element={<RecruitTalkingPoints />} />
              <Route path="nil" element={<NIL />} />
              <Route path="strength" element={<Strength />} />
              <Route path="medicine" element={<SportsMedicine />} />
              <Route path="operations" element={<Operations />} />
              <Route path="fundraising" element={<Fundraising />} />
              <Route path="communications" element={<Communications />} />
              <Route path="ticketing" element={<Ticketing />} />
              <Route path="marketing" element={<Marketing />} />
              <Route path="business" element={<BusinessOffice />} />
              <Route path="facilities" element={<Facilities />} />
              <Route path="wellness" element={<Wellness />} />
              <Route path="nutrition" element={<Nutrition />} />
              <Route path="security" element={<Security />} />
              <Route path="development" element={<Development />} />
              <Route path="video" element={<VideoCreative />} />
              <Route path="sports-info" element={<SportsInformation />} />
              <Route path="equipment" element={<Equipment />} />
              <Route path="audit" element={<AuditLog />} />
              <Route path="activity" element={<ActivityFeed />} />
              <Route path="messages" element={<Messages />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="tasks" element={<TasksPage />} />
              <Route path="staff" element={<Staff />} />
              <Route path="access" element={<StaffAccess />} />
              <Route path="sports/:sport" element={<SportPage />} />
              <Route path="sports/:sport/:tab" element={<SportPage />} />
              <Route path="settings" element={<Settings />} />
              <Route path="schools" element={<Schools />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
            </Suspense>
            </PageErrorBoundary>
            </ConfirmProvider>
        </BrowserRouter>
        </ChatProvider>
      </SchoolProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
